import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import CardGrid, { type CardItem } from '../components/CardGrid'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'

/**
 * "Modelos" — qué modelo de Bedrock usa cada paso del pipeline.
 *
 * Es de SOLO LECTURA a propósito. Los ids viven como constantes en
 * `stacks/main_stack.py` y llegan por variable de entorno; cambiar uno es un
 * deploy, porque además hay que ampliar la política de IAM de Bedrock — si no,
 * el `invoke_model` falla con AccessDenied. Un selector en el portal daría a
 * entender que el cambio aplica solo, y no aplica.
 */

interface ModelRow {
  step: string
  env_var: string
  model_id: string
  purpose: string
  configured: boolean
}

export default function Agents() {
  const [models, setModels] = useState<ModelRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    try {
      const res = await apiClient.get<{ models: ModelRow[] }>('/models')
      setModels(res.data.models ?? [])
      setError(null)
    } catch (err) {
      setError(errorMessage(err, 'No se pudieron cargar los modelos.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetch() }, [fetch])

  if (loading) return <LoadingSpinner label="Cargando modelos…" />

  const items: CardItem[] = models.map((m) => ({
    key: m.step,
    title: m.step,
    subtitle: m.purpose,
    status: m.configured
      ? <span className="badge-green">Activo</span>
      : <span className="badge-gray">Sin leer</span>,
    meta: [
      { label: 'Modelo', value: m.model_id || '—' },
      { label: 'Variable', value: m.env_var },
    ],
  }))

  return (
    <div className="flex flex-col gap-5">
      {error && <ErrorAlert message={error} onRetry={() => void fetch()} />}

      <p className="rounded-card border border-info-border bg-info-light px-4 py-3 text-body text-info">
        Los modelos se configuran en <b>la infraestructura</b>, no acá: cambiar uno requiere un
        deploy que además amplíe la política de IAM de Bedrock. Esta pantalla existe para saber
        qué está corriendo hoy en cada paso sin tener que leer el stack.
      </p>

      <CardGrid
        items={items}
        emptyTitle="No se pudieron leer los modelos"
        emptyMessage="El backend no devolvió ninguna variable de modelo configurada."
      />

      <div className="card p-5">
        <h2 className="text-[14px] font-extrabold text-ink">Restricciones que no se negocian</h2>
        <ul className="mt-3 flex flex-col gap-2 text-body text-ink-muted">
          <li className="flex gap-2">
            <span className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full bg-brand" />
            <span>
              <b className="text-ink">Ningún modelo de Anthropic.</b> El pipeline usa Gemma 3 en
              Bedrock con el esquema OpenAI <code className="font-mono text-micro">chat.completions</code>.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full bg-brand" />
            <span>
              Los ids de Gemma van <b className="text-ink">sin sufijo</b>{' '}
              <code className="font-mono text-micro">-v1:0</code>. Con el sufijo, el invoke
              devuelve <code className="font-mono text-micro">ValidationException</code>.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full bg-brand" />
            <span>
              Gemma inventa valores fuera de un enum: todo lo que devuelve se valida antes de
              persistirse.
            </span>
          </li>
        </ul>
      </div>
    </div>
  )
}
